import React from 'react';
import { HStack, IconButton, Tooltip, useToast, Icon } from '@chakra-ui/react';
import { IoLogoWhatsapp, IoLogoFacebook, IoLogoInstagram, IoShareSocial } from 'react-icons/io5';
import { SiTelegram } from 'react-icons/si';
import { getTranslations } from '../translations';

const ShareButtons = ({ isDarkMode }) => {
    const t = getTranslations();
    const toast = useToast();
    
    const appUrl = 'https://www.blograffo.net/workout/';
    const shareText = t.shareText || 'Workout Timer - Free timer for HIIT, Tabata, and Gym workouts';
    const shareTitle = t.shareTitle || 'Workout Timer';
    
    const handleShare = (platform) => {
        const encodedUrl = encodeURIComponent(appUrl);
        const encodedText = encodeURIComponent(shareText);
        
        let shareUrl = '';
        
        switch (platform) {
            case 'whatsapp':
                shareUrl = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
                window.open(shareUrl, '_blank', 'noopener,noreferrer');
                break;
                
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                window.open(shareUrl, '_blank', 'width=600,height=400,noopener,noreferrer');
                break;
                
            case 'instagram':
                // Instagram non supporta condivisione diretta via URL
                // Apriamo il profilo o usiamo un messaggio informativo
                toast({
                    title: t.shareInstagram || 'Share on Instagram',
                    description: t.shareInstagramToast || 'Copy the link and paste it in your story or post!',
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
                handleCopyLink();
                break;
                
            case 'telegram':
                shareUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
                window.open(shareUrl, '_blank', 'noopener,noreferrer');
                break;
                
            case 'generic':
                // Usa Web Share API se disponibile, altrimenti copia link
                if (navigator.share) {
                    navigator.share({
                        title: shareTitle,
                        text: shareText,
                        url: appUrl,
                    }).catch((err) => {
                        console.log('Errore condivisione:', err);
                        handleCopyLink();
                    });
                } else {
                    handleCopyLink();
                }
                break;
                
            default:
                break;
        }
    };
    
    const handleCopyLink = () => {
        navigator.clipboard.writeText(`${shareText} ${appUrl}`).then(() => {
            toast({
                title: t.linkCopied || 'Link copied!',
                description: t.linkCopiedDesc || 'The link has been copied to clipboard',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
        }).catch(() => {
            toast({
                title: t.shareError || 'Error',
                description: t.shareErrorDesc || 'Unable to copy link',
                status: 'error',
                duration: 2000,
                isClosable: true,
            });
        });
    };
    
    const buttonStyle = {
        size: 'lg',
        borderRadius: 'full',
        colorScheme: 'teal',
        variant: 'solid',
        _hover: {
            transform: 'scale(1.1)',
            transition: 'all 0.2s',
        },
    };
    
    const iconSize = '2em'; // Aumentata dimensione per icone più piene
    
    return (
        <HStack 
            spacing={4} 
            justify="center" 
            w="100%" 
            py={1}
            mt={-6}
            sx={{ marginTop: '-14px' }}
            flexWrap="wrap"
        >
            <Tooltip label={t.shareWhatsApp || 'Share on WhatsApp'} placement="top">
                <IconButton
                    aria-label={t.shareWhatsApp || 'Share on WhatsApp'}
                    icon={<Icon as={IoLogoWhatsapp} boxSize={iconSize} />}
                    onClick={() => handleShare('whatsapp')}
                    bg={isDarkMode ? 'green.600' : 'green.500'}
                    color="white"
                    _hover={{ bg: isDarkMode ? 'green.500' : 'green.600' }}
                    {...buttonStyle}
                />
            </Tooltip>
            
            <Tooltip label={t.shareFacebook || 'Share on Facebook'} placement="top">
                <IconButton
                    aria-label={t.shareFacebook || 'Share on Facebook'}
                    icon={<Icon as={IoLogoFacebook} boxSize={iconSize} />}
                    onClick={() => handleShare('facebook')}
                    bg={isDarkMode ? 'blue.700' : 'blue.600'}
                    color="white"
                    _hover={{ bg: isDarkMode ? 'blue.600' : 'blue.700' }}
                    {...buttonStyle}
                />
            </Tooltip>
            
            <Tooltip label={t.shareInstagram || 'Share on Instagram'} placement="top">
                <IconButton
                    aria-label={t.shareInstagram || 'Share on Instagram'}
                    icon={<Icon as={IoLogoInstagram} boxSize={iconSize} />}
                    onClick={() => handleShare('instagram')}
                    bgGradient={isDarkMode 
                        ? 'linear(to-r, purple.600, pink.600, orange.500)' 
                        : 'linear(to-r, purple.500, pink.500, orange.400)'}
                    color="white"
                    _hover={{ 
                        bgGradient: isDarkMode 
                            ? 'linear(to-r, purple.500, pink.500, orange.400)' 
                            : 'linear(to-r, purple.600, pink.600, orange.500)'
                    }}
                    {...buttonStyle}
                />
            </Tooltip>
            
            <Tooltip label={t.shareTelegram || 'Share on Telegram'} placement="top">
                <IconButton
                    aria-label={t.shareTelegram || 'Share on Telegram'}
                    icon={<Icon as={SiTelegram} boxSize={iconSize} />}
                    onClick={() => handleShare('telegram')}
                    bg={isDarkMode ? 'blue.500' : 'blue.400'}
                    color="white"
                    _hover={{ bg: isDarkMode ? 'blue.400' : 'blue.500' }}
                    {...buttonStyle}
                />
            </Tooltip>
            
            <Tooltip label={t.shareGeneric || 'Share'} placement="top">
                <IconButton
                    aria-label={t.shareGeneric || 'Share'}
                    icon={<Icon as={IoShareSocial} boxSize={iconSize} />}
                    onClick={() => handleShare('generic')}
                    bg={isDarkMode ? 'gray.600' : 'gray.500'}
                    color="white"
                    _hover={{ bg: isDarkMode ? 'gray.500' : 'gray.600' }}
                    {...buttonStyle}
                />
            </Tooltip>
        </HStack>
    );
};

export default ShareButtons;

